
import React, { useState, useMemo, useEffect } from 'react';
import { calculateFlowFromPressureDrop } from '../engine/hydraulics';
import { useLanguage } from '../i18n/LanguageContext';
import { getCalculatorToolText } from '../i18n/calculatorToolTranslations';
import { FlowRateReportData } from './PrintableReport';

interface FlowRateCalculatorProps {
  onPrint?: () => void;
  onReportDataChange?: (data: FlowRateReportData | null) => void;
}

export const FlowRateCalculator: React.FC<FlowRateCalculatorProps> = ({
  onPrint,
  onReportDataChange,
}) => {
  const { language } = useLanguage();
  const t = getCalculatorToolText(language);

  const [calcMode, setCalcMode] = useState<'from_velocity' | 'from_dp'>('from_velocity');
  const [rawId, setRawId] = useState<string>('26.2');
  const [rawVelocity, setRawVelocity] = useState<string>('1.5');
  const [rawDp, setRawDp] = useState<string>('20.0');
  const [rawLength, setRawLength] = useState<string>('30');

  const idMm = parseFloat(rawId);
  const vMs = parseFloat(rawVelocity);
  const dpKPa = parseFloat(rawDp);
  const lengthM = parseFloat(rawLength);

  const validationError = useMemo(() => {
    if (isNaN(idMm) || idMm <= 0) return t.invalidID;

    if (calcMode === 'from_velocity') {
      if (isNaN(vMs) || vMs <= 0) return t.invalidVelocity;
    } else {
      if (isNaN(dpKPa) || dpKPa <= 0) return t.invalidPressure;
      if (isNaN(lengthM) || lengthM <= 0) return t.invalidLength;
    }

    return null;
  }, [idMm, vMs, dpKPa, lengthM, calcMode, t]);

  const flowResult = useMemo(() => {
    if (validationError) return null;

    let m3s = 0;

    if (calcMode === 'from_velocity') {
      const dMeters = idMm / 1000;
      const area = (Math.PI * Math.pow(dMeters, 2)) / 4;
      m3s = vMs * area;
    } else {
      m3s = calculateFlowFromPressureDrop(dpKPa, lengthM, idMm, 0.0015, 20);
    }

    return {
      m3s,
      lps: m3s * 1000,
      m3h: m3s * 3600,
      gpmUs: m3s * 15850.323,
      gpmUk: m3s * 13198.15,
    };
  }, [calcMode, idMm, vMs, dpKPa, lengthM, validationError]);

  useEffect(() => {
    if (!onReportDataChange) return;

    if (validationError || !flowResult) {
      onReportDataChange(null);
      return;
    }

    onReportDataChange({
      calcMode,
      rawId,
      rawVelocity,
      rawDp,
      rawLength,
      results: flowResult,
    });
  }, [
    validationError,
    flowResult,
    calcMode,
    rawId,
    rawVelocity,
    rawDp,
    rawLength,
    onReportDataChange,
  ]);

  return (
    <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
      <div className="max-w-3xl">
        <span className="text-xs font-bold uppercase tracking-wider text-[#087F9C] block mb-1">
          {t.flowBadge}
        </span>

        <h2 className="font-display font-extrabold text-2xl text-[#10283D]">
          {t.flowTitle}
        </h2>

        <p className="text-sm text-slate-600 mt-1">
          {t.flowDescription}
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setCalcMode('from_velocity')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            calcMode === 'from_velocity'
              ? 'bg-[#10283D] text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          {t.modeVelocity}
        </button>

        <button
          onClick={() => setCalcMode('from_dp')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            calcMode === 'from_dp'
              ? 'bg-[#10283D] text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          {t.modePressure}
        </button>
      </div>

      {validationError && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-semibold">
          ⚠️ {validationError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.pipeID}
          </label>

          <input
            type="number"
            step="0.1"
            value={rawId}
            onChange={(e) => setRawId(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
          />

          <span className="text-[10px] text-slate-400 mt-1 block">
            {t.copperExample}
          </span>
        </div>

        {calcMode === 'from_velocity' ? (
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
              {t.fluidVelocity}
            </label>

            <input
              type="number"
              step="0.1"
              value={rawVelocity}
              onChange={(e) => setRawVelocity(e.target.value)}
              className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t.allowablePressure}
              </label>

              <input
                type="number"
                step="1"
                value={rawDp}
                onChange={(e) => setRawDp(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t.length}
              </label>

              <input
                type="number"
                step="1"
                value={rawLength}
                onChange={(e) => setRawLength(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
              />
            </div>
          </div>
        )}
      </div>

      {flowResult ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-200">
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block">
              {t.litresSecond}
            </span>
            <span className="font-display font-extrabold text-2xl text-[#10283D]">
              {flowResult.lps.toFixed(3)} L/s
            </span>
          </div>

          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block">
              {t.cubicMetresHour}
            </span>
            <span className="font-display font-extrabold text-2xl text-[#10283D]">
              {flowResult.m3h.toFixed(2)} m³/h
            </span>
          </div>

          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block">
              {t.usGallons}
            </span>
            <span className="font-display font-extrabold text-2xl text-[#087F9C]">
              {flowResult.gpmUs.toFixed(2)} GPM
            </span>
          </div>

          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block">
              {t.ukGallons}
            </span>
            <span className="font-display font-extrabold text-2xl text-[#10283D]">
              {flowResult.gpmUk.toFixed(2)} GPM
            </span>
          </div>
        </div>
      ) : null}

      {onPrint && flowResult && (
        <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-slate-500 font-medium">
            Generate verified pipe flow rate &amp; hydraulic capacity report
          </span>
          <button
            type="button"
            onClick={onPrint}
            disabled={!!validationError}
            className="py-2.5 px-5 rounded-2xl font-bold text-xs bg-[#10283D] hover:bg-[#1E3A5F] active:bg-[#087F9C] text-white shadow-md flex items-center gap-2 transition-all duration-200 border border-slate-700/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="w-4 h-4 text-[#39BFD3]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            <span>PDF Report / Print Summary</span>
          </button>
        </div>
      )}
    </div>
  );
};
